import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../layout/AdminLayout';
import {
  getReviewPermission,
  getAllBots,
  advanceBot,
  adminEditReview,
  verifyProvider,
  ReviewBot,
} from '../../services/providerReviewService';
import BotTable from './BotTable';
import { LIFECYCLE, ALL_STATUSES } from './botStatus';
import './BotReviewPage.css';

/**
 * Cross-provider all-bots page: bots never disappear here — Published bots stay
 * visible and can be suspended anytime; the status FILTER narrows, not hides.
 */
const AllBotsPage: React.FC = () => {
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [bots, setBots] = useState<ReviewBot[]>([]);
  const [status, setStatus] = useState('all');
  const [entry, setEntry] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (s: string) => {
    setLoading(true);
    setError(null);
    try {
      setBots(await getAllBots(s));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getReviewPermission().then((ok) => {
      setAllowed(ok);
      if (ok) load(status);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const onFilter = (s: string) => { setStatus(s); load(s); };

  const act = async (fn: () => Promise<void>, key: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load(status);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (allowed === null) {
    return <AdminLayout><div className="review-wrap">…</div></AdminLayout>;
  }
  if (!allowed) {
    return (
      <AdminLayout>
        <div className="review-wrap">
          <h2>{t('allBots.title', 'All Bots')}</h2>
          <p className="review-denied">{t('review.denied', 'You do not have bot-review permission.')}</p>
        </div>
      </AdminLayout>
    );
  }

  // Funnel view: entryChannel filter is client-side (the list is one Scan),
  // with per-channel counts so conversion by marketing entry is visible at a
  // glance. Bots created before the field existed group under '—'.
  const channelOf = (b: ReviewBot) => b.entryChannel || '—';
  const channels = Array.from(new Set(bots.map(channelOf))).sort();
  const counts = bots.reduce<Record<string, number>>((acc, b) => {
    acc[channelOf(b)] = (acc[channelOf(b)] || 0) + 1;
    return acc;
  }, {});
  const visible = entry === 'all' ? bots : bots.filter((b) => channelOf(b) === entry);

  return (
    <AdminLayout>
      <div className="review-wrap">
        <div className="review-head">
          <div>
            <h2>{t('allBots.title', 'All Bots')}</h2>
            <p className="review-sub">{t('allBots.subtitle', 'Every bot across all providers, including published ones.')}</p>
          </div>
          <div className="allbots-controls">
            <select value={entry} onChange={(e) => setEntry(e.target.value)} disabled={loading}>
              <option value="all">{t('allBots.allEntries', 'All entries')}</option>
              {channels.map((c) => (
                <option key={c} value={c}>{c} ({counts[c]})</option>
              ))}
            </select>
            <select value={status} onChange={(e) => onFilter(e.target.value)} disabled={loading}>
              <option value="all">{t('allBots.all', 'All statuses')}</option>
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`status.${s}`, s)}</option>
              ))}
            </select>
            <button className="review-refresh" onClick={() => load(status)} disabled={loading}>
              {t('review.refresh', 'Refresh')}
            </button>
          </div>
        </div>
        {error && <div className="review-error">{error}</div>}
        {loading ? (
          <p>…</p>
        ) : visible.length === 0 ? (
          <p className="review-empty">{t('allBots.empty', 'No bots.')}</p>
        ) : (
          <BotTable
            bots={visible}
            actionsFor={(s) => LIFECYCLE[s] || []}
            busy={busy}
            onAction={(botId, to, note, reasons) => act(() => advanceBot(botId, to, note, reasons), botId + to)}
            onEditReview={(botId, approve, note, reasons) => act(() => adminEditReview(botId, approve, note, reasons), botId + (approve ? 'editApprove' : '__rejectEdit'))}
            onVerify={(providerId) => act(() => verifyProvider(providerId), 'v' + providerId)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AllBotsPage;
